import dynamic from "next/dynamic";
import { getRandom } from "../../utils/blogUtils";
import blogData from "../../blogs.json";
import Service2 from "../../components/services/Service2";
const Capabilities2 = dynamic(() => import("../../components/services/Capabilities2"), { ssr: false });
import SuccessStoryEmbed from "../../components/trust/SuccessStoryEmbed";
import MethodologyAnchor from "../../components/services/MethodologyAnchor";
import Head from "next/head";
import Link from "next/link";
import ServiceFAQ from "../../components/services/ServiceFAQ";
import ServiceEngagementLinks from "../../components/services/ServiceEngagementLinks";
import { CustomerSection } from "../../components/ai-agent-demo/CustomerSection";

const ContactUsHome = dynamic(() => import("../../components/homepage/ContactUsHome"), { ssr: false });
const Footer = dynamic(() => import("../../components/Footer"), { ssr: false });

iotAndDigitalEng.title = "IoT & Edge AI Engineering for Industrial Operations | ITMTB";
iotAndDigitalEng.desc =
  "ITMTB builds IoT and edge AI systems for industrial operations — connected factory intelligence, predictive maintenance, real-time sensor data platforms, and Industry 4.0 deployments.";
iotAndDigitalEng.header_img = "https://www.itmtb.com/services/capability/iot.webp";
iotAndDigitalEng.keywords =
  "IoT consulting India manufacturing, edge AI India industrial, Industry 4.0 implementation India, IoT sensor data platform India, predictive maintenance AI India, connected factory India, AWS IoT Core Azure IoT Hub India";

const faqItems = [
  {
    q: "What is edge AI for manufacturing and how does it differ from cloud AI?",
    a: "Cloud AI sends data to a remote server for inference and receives results back — this introduces latency (typically 50–500ms round-trip) and requires reliable network connectivity. Edge AI runs inference on a device physically close to or on the machine generating the data — latency drops to 1–10ms, and the system works even when network connectivity is degraded. For manufacturing, the distinction matters for: quality control (vision-based defect detection needs sub-10ms to keep up with production line speeds); safety monitoring (operator proximity detection can't wait for a cloud round-trip); predictive maintenance (running anomaly detection on sensor streams locally rather than batch-uploading to cloud). The hardware for edge inference ranges from NVIDIA Jetson modules for vision workloads to microcontrollers running TensorFlow Lite for simpler anomaly detection on sensor data. We select the right inference hardware for the latency and power constraints of your specific use case.",
  },
  {
    q: "What is a digital twin and when does it make sense to build one?",
    a: "A digital twin is a software model of a physical asset (a machine, a production line, a facility) that is updated in real time from sensor data, enabling you to simulate, predict, and optimise the physical system without interrupting it. The business cases where a digital twin has clear ROI: predictive maintenance (the twin predicts failure before it occurs by modelling wear patterns across the sensor data history); capacity optimisation (simulating production schedule changes against the twin before committing to them physically); remote monitoring (inspecting the state of equipment across multiple sites without physical presence); and quality control (correlating production conditions at the moment of a defect with the twin's state to identify the root cause faster). Digital twins are not universally worth building — they require good sensor coverage, reliable data pipelines, and ongoing calibration. We assess fit during the discovery sprint rather than recommending them as a default.",
  },
  {
    q: "How do IoT systems connect to ERP and production management software?",
    a: "The integration between IoT sensor data and ERP (SAP, Dynamics 365, Odoo) or MES (Manufacturing Execution Systems) is the layer where most industrial IoT projects stall. IoT generates high-frequency time-series data (thousands of events per second per machine); ERPs and MES expect structured, lower-frequency transactional records. Bridging them requires: an IoT data platform (AWS IoT Core, Azure IoT Hub, or an on-premises MQTT broker) to receive and buffer raw sensor streams; a transformation layer that aggregates sensor events into business-meaningful records (e.g., shift production summaries, quality hold events, maintenance alerts); and an integration service that writes the transformed records into the ERP or MES in the format those systems expect. OPC-UA is the standard protocol for connecting to industrial control systems (PLCs, SCADA) that most manufacturing ERPs also support. We architect and build this integration stack as a defined deliverable — not an afterthought added after the IoT devices are already deployed.",
  },
  {
    q: "What protocols does ITMTB use for industrial IoT deployments?",
    a: "Protocol selection depends on the industrial environment and device constraints. MQTT is the standard for internet-connected IoT devices: lightweight, reliable over unreliable networks, widely supported by cloud IoT platforms (AWS IoT Core, Azure IoT Hub, Google Cloud IoT). OPC-UA is the standard for industrial automation: used by PLCs, SCADA systems, and modern CNC machines — it provides standardised read/write access to machine data. For legacy equipment that only exposes Modbus or other older protocols, we deploy protocol translation gateways (often running on edge devices) that convert legacy protocols to MQTT or OPC-UA for the rest of the architecture. For time-series data storage: Apache Kafka or AWS Kinesis for high-throughput streaming; InfluxDB or TimescaleDB for time-series querying; object storage (S3, Azure Blob) for raw sensor archive. We select the protocol and storage stack based on your device ecosystem and data volume, not based on a preferred technology.",
  },
  {
    q: "How does ITMTB price an engagement?",
    a: "Pricing follows the engagement model. Discovery sprints are fixed-price for a fixed timebox. Build engagements are scoped after discovery — fixed-scope, time-and-materials, or outcome-linked. Managed services are billed monthly with defined SLAs. Total cost depends on scope, integration complexity, QA depth, and post-launch support — we publish a transparent breakdown for buyers evaluating their options.",
    link: "/blog/custom-software-development-cost-india",
    linkLabel: "Read the cost guide",
  },
  {
    q: "What does an engagement with ITMTB look like?",
    a: "Every engagement starts with a fixed-scope, fixed-price discovery sprint — typically 2 weeks. You leave with a written roadmap, risk register, cost estimate, and timeline before committing to a build. We then offer four engagement models: fixed-scope builds, time-and-materials, outcome-linked retainers, and managed services post-launch. If discovery doesn't justify proceeding, you can stop there — no obligation.",
    link: "/itmtb-how-we-work",
    linkLabel: "See how we work",
  },
];
iotAndDigitalEng.faq = faqItems;

function iotAndDigitalEng() {
  let insights = blogData.filter(item => !["case-study", "tech"].includes(item?.meta?.category));
  let caseStudies = blogData.filter(item => item?.meta?.category === "case-study");

  let filteredInsights = insights.filter(item => item?.meta?.refer?.includes("IoT and Digital Engineering"));
  let filteredCaseStudies = caseStudies.filter(item => item?.meta?.refer?.includes("IoT and Digital Engineering"));

  if (filteredInsights.length === 1) {
    insights = [...filteredInsights, ...getRandom(insights.filter(item => !filteredInsights.includes(item)), 1)];
  } else if (filteredInsights.length >= 2) {
    insights = getRandom(filteredInsights, 2);
  } else {
    insights = getRandom(insights, 2);
  }

  if (filteredCaseStudies.length === 1) {
    caseStudies = [...filteredCaseStudies, ...getRandom(caseStudies.filter(item => !filteredCaseStudies.includes(item)), 1)];
  } else if (filteredCaseStudies.length >= 2) {
    caseStudies = getRandom(filteredCaseStudies, 2);
  } else {
    caseStudies = getRandom(caseStudies, 2);
  }

  const subServices = [
    {
      img: null,
      key: "Connected Factory Intelligence",
      title: "Plant floor data — from PLCs, sensors, and SCADA — made accessible, structured, and actionable.",
      text: "We connect industrial equipment to a modern data platform using OPC-UA for automation systems and MQTT for internet-connected devices. Raw sensor streams are aggregated and structured into shift-level and event-level records that operations teams can actually use — and that ERP and MES systems can ingest.\n\nFor legacy equipment that only exposes Modbus or proprietary protocols, we deploy edge gateway devices that translate to standard protocols, preserving your existing capital equipment investment while making its data accessible.",
    },
    {
      img: null,
      key: "Edge AI and Real-Time Decision Making",
      title: "Intelligence where it's produced — inference at the edge, not the cloud.",
      text: "For use cases where cloud round-trip latency is too slow — quality control on production lines, safety monitoring, real-time anomaly detection — we deploy AI inference at the edge using NVIDIA Jetson modules for vision workloads and TensorFlow Lite for sensor-based anomaly detection on constrained hardware.\n\nEdge AI systems work when network connectivity is degraded, which matters for industrial environments where reliable connectivity to cloud cannot be guaranteed during operations.",
    },
    {
      img: null,
      key: "Predictive Maintenance and Digital Twin",
      title: "Predict equipment failure before it happens. Simulate production changes before you commit to them.",
      text: "Predictive maintenance models train on historical sensor data (vibration, temperature, pressure, current draw) to identify the early signatures of component failure — typically catching failures days to weeks before they would cause unplanned downtime. We build these models, connect them to your sensor streams, and wire alerts into your maintenance workflow.\n\nDigital twins extend this: a real-time software model of a physical asset that enables capacity simulation, remote monitoring across sites, and faster root-cause analysis when defects occur.",
    },
    {
      img: null,
      key: "IoT Data Platform and Pipeline Engineering",
      title: "High-frequency sensor streams ingested, structured, and made queryable — at scale.",
      text: "IoT deployments at industrial scale generate millions of events per hour. The data platform architecture has to handle the ingest volume, store efficiently for time-series querying, and route events to downstream systems in real time.\n\nWe architect and build IoT data platforms on AWS IoT Core, Azure IoT Hub, or on-premises MQTT brokers — with time-series storage (InfluxDB, TimescaleDB), streaming pipelines (Kafka, Kinesis), and integration adapters for ERP and MES systems.",
    },
    {
      img: null,
      key: "Smart Product and Connected Device Development",
      title: "Building intelligence into physical products — from firmware to cloud backend to mobile interface.",
      text: "For product companies adding connectivity to hardware — consumer devices, industrial equipment, medical devices — we develop the full software stack: embedded firmware (RTOS, Linux-based, FreeRTOS), device management platform (OTA updates, fleet monitoring), cloud backend for data aggregation and product analytics, and customer-facing mobile or web applications.\n\nConnected product development requires firmware, cloud, and application expertise simultaneously. We bring all three rather than requiring you to coordinate separate vendors for each layer.",
    },
  ];

  return (
    <div>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ProfessionalService",
              name: "IoT & Edge AI Engineering for Industrial Operations India",
              url: "https://www.itmtb.com/services/iot-and-digital-eng",
              image: "https://www.itmtb.com/services/capability/iot.webp",
              description:
                "ITMTB builds IoT and edge AI systems for industrial operations — connected factory intelligence, predictive maintenance, real-time sensor data platforms, and Industry 4.0 deployments.",
              provider: {
                "@type": "Organization",
                name: "ITMTB Technologies",
                url: "https://www.itmtb.com",
                logo: "https://www.itmtb.com/logo.webp",
              },
              areaServed: [{ "@type": "Place", name: "India" }],
              serviceType: [
                "IoT Consulting India Manufacturing",
                "Edge AI Industrial India",
                "Industry 4.0 Implementation",
                "IoT Sensor Data Platform",
                "Predictive Maintenance AI",
                "Connected Factory India",
                "AWS IoT Core Consulting",
                "Azure IoT Hub Consulting",
              ],
              sameAs: [
                "https://www.linkedin.com/company/itmtb-technologies/",
                "https://x.com/ITMTBTech",
                "https://www.instagram.com/itmtbtech/",
              ],
            }),
          }}
        />
      </Head>

      <Service2
        heading="IoT and Edge AI Engineering"
        paragraph={`Industrial operations generate data at the machine level that most businesses can't yet act on — because it's locked in PLCs, SCADA systems, and legacy equipment that wasn't designed to share it.

We connect that data to modern analytics and AI systems: building IoT platforms that ingest high-frequency sensor streams, edge AI systems that run inference where the data is produced, and predictive maintenance models that flag equipment failure before it causes unplanned downtime.

For a global logistics company, we deployed a supply chain technology platform integrating Dynamics 365 across operations — unifying inventory visibility across multiple sites and enabling data-driven operational decisions that weren't possible with siloed systems.`}
        bgPath="/services/capability/iot.webp"
        primaryCta={{ label: "Talk to us →", href: "/contact-us" }}
      />

      <SuccessStoryEmbed
        referTag="IoT and Digital Engineering"
        maxItems={3}
        heading="Selected work in IoT and digital engineering"
      />

      <CustomerSection title="Trusted by" />

      <MethodologyAnchor />

      <Capabilities2
        dataList={subServices}
        bg="bg-[#402A6A]"
        insights={[...caseStudies, ...insights]}
        hideFooter
        hideCardSubtitle
      />

      <section className="px-8 md:px-[60px] py-8 bg-white border-t border-gray-100">
        <p className="max-w-4xl text-sm text-gray-500 leading-relaxed">
          IoT and digital engineering is one of the specialised practices within our broader{" "}
          <Link href="/services/custom-software-development">
            <a className="text-indigo-600 font-medium hover:text-indigo-800 transition-colors">custom software development</a>
          </Link>{" "}
          capability, alongside platform engineering, application development, and AI-enabled systems.
        </p>
      </section>

      <ServiceFAQ items={faqItems} />

      <ServiceEngagementLinks />

      <ContactUsHome />

      <Footer />
    </div>
  );
}

export default iotAndDigitalEng;
